import { Col } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useQueryClient } from "react-query";

import { VerificationStatus } from "@/apis/IdentityCheckHelper";
import ErrorBanner from "@/components/Commons/ErrorBanner";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import { setLastFailedIdvType } from "@/components/IdentityCheck/helper/rivHelper";
import { USER_PROGRESS_QUERY_KEY } from "@/hooks/useUserProgress";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

export const DATA_TEST_ID_UNABLE_TO_VERIFY = "UnableToVerify";
export const DATA_TEST_ID_UNABLE_TO_VERIFY_FALLBACK_OPTION =
  "UnableToVerifyFallbackOption";

const ITSupportLink = ({
  verificationStatus,
}: {
  verificationStatus: VerificationStatus;
}): JSX.Element => {
  switch (verificationStatus) {
    case VerificationStatus.LIGHT_FAIL:
      return (
        <ChatWithITSupportFlyoutLink
          localeMessageId="identityCheck-errors-chatWithIT"
          metricProps={{
            category: Day1EventCategory.IDCheckLightWait,
            action: Day1EventAction.ChatWithSupport,
            label: Day1EventLabel.IDCheckLightFail,
          }}
        />
      );
    case VerificationStatus.FULL_FAIL:
      return (
        <ChatWithITSupportFlyoutLink
          localeMessageId="identityCheck-errors-chatWithIT"
          metricProps={{
            category: Day1EventCategory.IDCheckFullWait,
            action: Day1EventAction.ChatWithSupport,
            label: Day1EventLabel.IDCheckFullFail,
          }}
        />
      );
    default:
      return (
        <ChatWithITSupportFlyoutLink localeMessageId="identityCheck-errors-chatWithIT" />
      );
  }
};

/**
 * Message to prompt users to contact IT support for manual IDV.
 */
const ContactITSupport = ({
  docsVerificationStatus,
}: {
  docsVerificationStatus: VerificationStatus;
}): JSX.Element => {
  return (
    <div data-test-id={DATA_TEST_ID_UNABLE_TO_VERIFY}>
      <ErrorBanner>
        <FormattedMessage
          id="identityCheck-errors-unableToVerify"
          values={{
            link: <ITSupportLink verificationStatus={docsVerificationStatus} />,
          }}
        />
      </ErrorBanner>
    </div>
  );
};

const NotIdvEligibleError = () => {
  return (
    <ErrorBanner>
      <Col gridGap="S300">
        <Text>
          <FormattedMessage
            id="error-notVerifiableError-text1"
            values={{
              chatWithITSupportLink: (
                <ChatWithITSupportFlyoutLink
                  localeMessageId="text-chatWithITSupport"
                  metricProps={{
                    category: Day1EventCategory.BlockingError,
                    action: Day1EventAction.ChatWithSupport,
                    label: Day1EventLabel.GenericError,
                  }}
                />
              ),
            }}
          />
        </Text>
      </Col>
    </ErrorBanner>
  );
};

/**
 * Returns an unable to verify message.
 * Reads AppContext.docsVerificationStatus to determine the last type of document verification performed before a failure caused the UnableToVerify message to be shown.
 *  This configures metrics for the resultant ITChatSupportLink.
 * @constructor
 */
export const UnableToVerify = ({
  allowAutomatedIdvFallback,
}: {
  allowAutomatedIdvFallback?: boolean;
}): JSX.Element => {
  const { state, dispatch } = useContext(AppContext);
  const queryClient = useQueryClient();

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    switch (state.docsVerificationStatus) {
      case VerificationStatus.LIGHT_FAIL:
        dispatch({
          type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
          chatSupportRefPage: "ezo-identity-check-lightweight-fail",
        });
        break;
      case VerificationStatus.FULL_FAIL:
        dispatch({
          type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
          chatSupportRefPage: "ezo-identity-check-full-fail",
        });
        break;
      default:
        // do nothing
        break;
    }
  }, [state.docsVerificationStatus]);

  useEffect(() => {
    switch (state.docsVerificationStatus) {
      case VerificationStatus.LIGHT_FAIL:
        dispatch({
          type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
          nonDirectedChatSupportContext: "ON_LIGHT_RIV_RESULT_FAIL",
        });
        break;
      case VerificationStatus.FULL_FAIL:
        dispatch({
          type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
          nonDirectedChatSupportContext: "ON_FULL_RIV_RESULT_FAIL",
        });
        break;
      default:
        // do nothing
        break;
    }
  }, [state.docsVerificationStatus]);

  /**
   * Rerun the userProgress API to update the tracking verification status state in the app.
   */
  useEffect(() => {
    setLastFailedIdvType("manual");
    void queryClient.invalidateQueries(USER_PROGRESS_QUERY_KEY);
  }, [queryClient]);

  const [emitNotVerifiableMetric, setEmitNotVerifiableMetric] = useState(true);
  if (state.docsVerificationStatus === VerificationStatus.NOT_VERIFIABLE) {
    if (emitNotVerifiableMetric) {
      // Track that the no supported IDV methods banner is shown
      trackStructEvent({
        category: Day1EventCategory.IDVPrefSelect,
        action: Day1EventAction.NotVerifiable,
      });
      // set to false to prevent re-renders from spamming metric
      setEmitNotVerifiableMetric(false);
    }

    return <NotIdvEligibleError />;
  }

  // They are eligible for Automated Verification as fallback.
  if (allowAutomatedIdvFallback) {
    return (
      <div data-test-id={DATA_TEST_ID_UNABLE_TO_VERIFY_FALLBACK_OPTION}>
        <ErrorBanner>
          <FormattedMessage
            id="identityCheck-errors-unableToVerifyFallback"
            values={{
              b: (chunk: unknown) => {
                return <b>{chunk}</b>;
              },
              a: (chunk: unknown) => {
                return (
                  <Link
                    onClick={() => {
                      // TODO: Metrics agreement + collection on click.

                      // Set the wizard path to the initial step of IVV.
                      dispatch({
                        type: ActionType.SET_WIZARD_PATH,
                        wizardPath: "IVV",
                      });

                      // This is used purely to control the UI even though technically they did start and fail.
                      dispatch({
                        type: ActionType.SET_DOCS_VERIFICATION_STATUS,
                        docsVerificationStatus: VerificationStatus.NOT_STARTED,
                      });
                    }}
                    href="#"
                    data-test-id="SetWizardPathLink"
                    className="message-banner-link"
                  >
                    {chunk}
                  </Link>
                );
              },
            }}
          />
        </ErrorBanner>
      </div>
    );
  } else {
    return (
      <ContactITSupport docsVerificationStatus={state.docsVerificationStatus} />
    );
  }
};
